import React, { useEffect, useState } from 'react'
import {
  CBadge, CCard, CCardBody, CCardHeader, CCol, CRow,
} from '@coreui/react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import { estadoEmpresa } from '../utils/estadoEmpresa'
import { planEmpresaLabel } from '../utils/planEmpresa'

// Panel del superadmin — ruta /superadmin (Resumen del menú ADMIN).
// El breadcrumb lo muestra el AppHeader global (AppBreadcrumb); el título
// de página va debajo. Las demás secciones del menú son rutas propias
// (/superadmin/empresas, /superadmin/usuarios, ...) — sin estado local.
const SuperAdminPage: React.FC = () => {
  const navigate = useNavigate()
  const [dash, setDash] = useState<any | null>(null)
  const [error, setError] = useState('')
  // Sesiones: GET /api/superadmin/sesiones
  const [sesiones, setSesiones] = useState<any[]>([])
  // [RGM] 2026-09-17 (pedido Rafael: "en resumen superadmin debe aparecer
  // Empresas después de los KPIs… mejor ver empresas al inicio"): listado de
  // empresas en el Resumen, entre los KPIs y las sesiones.
  const [empresas, setEmpresas] = useState<any[]>([])

  useEffect(() => {
    api.get<any>('/superadmin/dashboard').then(setDash).catch((e: any) => setError(e.message))
    api.get<any[]>('/superadmin/sesiones').then(setSesiones).catch((e: any) => setError(e.message))
    api.get<any[]>('/superadmin/empresas').then(setEmpresas).catch((e: any) => setError(e.message))
  }, [])

  // KPIs del panel — indicadores GLOBALES de plataforma (no de una empresa).
  // Primera fila: empresas. Segunda fila: usuarios, planes y módulos.
  // "Empresas en prueba" = EmpresaEstado 2|6 (setup pendiente, home.cs:2443-2450).
  const filaEmpresas = [
    { label: 'Empresas totales', valor: dash?.totalEmpresas, color: 'text-primary' },
    { label: 'Empresas activas', valor: dash?.empresasActivas, color: 'text-success' },
    { label: 'Empresas en prueba', valor: dash?.empresasEnPrueba, color: 'text-warning' },
    { label: 'Empresas suspendidas', valor: dash?.empresasSuspendidas, color: 'text-danger' },
  ]
  const filaOperacion = [
    { label: 'Usuarios totales', valor: dash?.totalUsuarios, color: 'text-info' },
    { label: 'Usuarios online', valor: dash?.usuariosOnline, color: 'text-warning' },
    { label: 'Planes activos', valor: dash?.planesActivos, color: 'text-primary' },
    { label: 'Módulos contratados', valor: dash?.modulosContratados, color: 'text-success' },
  ]

  const columnasSesiones: WwColumn[] = [
    { key: 'usuarioCodigo', title: 'Usuario' },
    { key: 'usuarioNombre', title: 'Nombre' },
    { key: 'empresaCodigo', title: 'Empresa' },
    { key: 'rolNombre', title: 'Rol' },
    { key: 'usuarioEstado', title: 'Estado' },
  ]

  // [RGM] 2026-09-17 (pedido Rafael): mismas etiquetas de la grilla de
  // Empresas (Plan en texto + Estado con badge GX) para el Resumen.
  const columnasEmpresas: WwColumn[] = [
    { key: 'empresaCodigo', title: 'Código' },
    { key: 'empresaNombre', title: 'Empresa' },
    {
      key: 'empresaEstado', title: 'Estado',
      render: (row) => {
        const e = estadoEmpresa(Number(row.empresaEstado ?? 0))
        return <CBadge color={e.color}>{e.label}</CBadge>
      },
      // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
      // exportValue espeja el render (mismo estadoEmpresa().label).
      exportValue: (row) => estadoEmpresa(Number(row.empresaEstado ?? 0)).label,
    },
    {
      key: 'empresaLibreNumerico2', title: 'Plan',
      render: (row) => planEmpresaLabel(row.empresaLibreNumerico2),
      // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
      // exportValue espeja el render (mismo planEmpresaLabel).
      exportValue: (row) => planEmpresaLabel(row.empresaLibreNumerico2),
    },
    { key: 'rutas', title: 'Rutas', type: 'number' },
    { key: 'clientes', title: 'Clientes', type: 'number' },
    { key: 'tarifa', title: 'Tarifa mensual', type: 'currency' },
    { key: 'empresaProximoCobro', title: 'Próximo cobro', type: 'date' },
  ]

  const KpiFila = ({ fila }: { fila: { label: string; valor: any; color: string }[] }) => (
    <CRow className="g-3 mb-4">
      {fila.map((k) => (
        <CCol key={k.label} md={3} sm={6}>
          <CCard className="text-center h-100 shadow-sm">
            <CCardBody>
              <div className={`fs-3 fw-bold ${k.color}`}>{k.valor ?? '—'}</div>
              <div className="text-body-secondary small">{k.label}</div>
            </CCardBody>
          </CCard>
        </CCol>
      ))}
    </CRow>
  )

  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4 className="mb-0 fw-bold">Panel</h4>
        </CCol>
      </CRow>

      {error && <div className="alert alert-danger">{error}</div>}

      <KpiFila fila={filaEmpresas} />
      <KpiFila fila={filaOperacion} />

      {/* [RGM] 2026-09-17 (pedido Rafael): Empresas ARRIBA (después de los
          KPIs) — clic en una fila lleva al panel de Empresas. */}
      <CCard className="shadow-sm mb-4">
        <CCardHeader className="fw-semibold">Empresas</CCardHeader>
        <CCardBody>
          <WwGrid
            title="Empresas"
            columns={columnasEmpresas}
            rows={empresas}
            onRowClick={() => navigate('/superadmin/empresas')}
          />
        </CCardBody>
      </CCard>

      <CCard className="shadow-sm">
        <CCardHeader className="fw-semibold">Sesiones activas</CCardHeader>
        <CCardBody>
          {sesiones.length === 0
            ? <div className="text-body-secondary py-3 text-center">Nadie conectado</div>
            : <WwGrid title="Sesiones" columns={columnasSesiones} rows={sesiones} />}
        </CCardBody>
      </CCard>
    </>
  )
}

export default SuperAdminPage
